import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  alpha,
  FormControl,
  Select,
  MenuItem,
  Button,
  Chip,
  Divider,
  InputLabel,
} from '@mui/material';
import { TimelineRounded, RefreshRounded, SensorsRounded, DownloadRounded } from '@mui/icons-material';
import api, { errorMessage } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { palette } from '../theme';
import { GlassPanel, SectionTitle, EmptyState, PanelLoader, StatusPill } from '../components/ui/GlassPanel';
import { HoloLineChart } from '../components/charts/HoloChart';
import { useToast } from '../components/ui/ToastProvider';

const RANGES = ['1H', '6H', '24H', '7D', '30D'];

export default function DeviceData() {
  const toast = useToast();
  const [devices, setDevices] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [range, setRange] = useState('24H');
  const [history, setHistory] = useState({ series: [], stats: {}, keys: [] });
  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(false);

  useEffect(() => {
    const loadDevices = async () => {
      try {
        const response = await api.get('/devices/', { params: { limit: 500 } });
        const list = response.data || [];
        setDevices(list);
        if (list.length && !selectedId) setSelectedId(list[0].device_id);
      } catch (error) {
        toast.error(errorMessage(error, 'Unable to load the device list.'));
      } finally {
        setLoading(false);
      }
    };
    loadDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTelemetry = useCallback(async () => {
    if (!selectedId) return;
    setChartLoading(true);
    try {
      const [historyResponse, latestResponse] = await Promise.allSettled([
        api.get(`/telemetry/device/${selectedId}/history`, { params: { range } }),
        api.get(`/telemetry/device/${selectedId}/latest`),
      ]);
      setHistory(historyResponse.status === 'fulfilled' ? historyResponse.value?.data || { series: [], stats: {}, keys: [] } : { series: [], stats: {}, keys: [] });
      setLatest(latestResponse.status === 'fulfilled' ? latestResponse.value?.data || null : null);
    } finally {
      setChartLoading(false);
    }
  }, [selectedId, range]);

  useEffect(() => {
    loadTelemetry();
  }, [loadTelemetry]);

  const chart = useMemo(() => {
    const series = history.series || [];
    const keys = history.keys || [];
    const preferred = keys.filter((key) =>
      ['temperature', 'humidity', 'battery', 'pressure', 'signal', 'voltage'].includes(key.toLowerCase())
    );
    const selected = (preferred.length ? preferred : keys).slice(0, 4);

    const labels = series.map((point) => {
      const timestamp = point.timestamp ? new Date(point.timestamp) : null;
      if (!timestamp || Number.isNaN(timestamp.getTime())) return '';
      return ['1H', '6H', '24H'].includes(range)
        ? timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : timestamp.toLocaleDateString([], { day: '2-digit', month: 'short' });
    });

    const colors = [palette.primary, palette.secondary, palette.success, palette.warning];

    return {
      labels,
      datasets: selected.map((key, index) => ({
        label: key,
        data: series.map((point) => (point[key] === undefined ? null : point[key])),
        color: colors[index % colors.length],
      })),
    };
  }, [history, range]);

  const latestRows = useMemo(() => {
    if (!latest?.timestamp) return [];
    return Object.entries(latest.data || {}).map(([key, value]) => ({
      key,
      value: typeof value === 'number' ? Number(value.toFixed(3)) : value,
    }));
  }, [latest]);

  const exportCsv = () => {
    if (!history.series?.length) return;
    const keys = history.keys || [];
    const header = ['timestamp', ...keys].join(',');
    const rows = history.series.map((point) =>
      [point.timestamp, ...keys.map((key) => point[key] ?? '')].join(',')
    );
    const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${selectedId}_${range}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Telemetry export generated.');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <GlassPanel sx={{ p: 3 }}>
        <SectionTitle
          title="Device Data Explorer"
          subtitle="Select an endpoint and inspect its live and historical telemetry"
          icon={<TimelineRounded sx={{ fontSize: 18 }} />}
          action={
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadRounded sx={{ fontSize: 15 }} />}
              onClick={exportCsv}
              disabled={!history.series?.length}
            >
              Export CSV
            </Button>
          }
        />

        <Box sx={{ display: 'flex', gap: 2, mt: 2.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>Device</InputLabel>
            <Select label="Device" value={selectedId} onChange={(event) => setSelectedId(event.target.value)}>
              {devices.map((device) => (
                <MenuItem key={device.device_id} value={device.device_id}>
                  {device.device_id} — {(device.properties || {}).device_name || device.device_type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {RANGES.map((option) => (
              <Button
                key={option}
                size="small"
                variant={range === option ? 'contained' : 'outlined'}
                onClick={() => setRange(option)}
                sx={{ minWidth: 50, py: 0.55, fontSize: '0.72rem' }}
              >
                {option}
              </Button>
            ))}
          </Box>

          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={loadTelemetry} sx={{ color: 'text.secondary' }}>
            <RefreshRounded sx={{ fontSize: 16, mr: 0.75, animation: chartLoading ? 'holoSpin 900ms linear infinite' : 'none' }} />
            Refresh
          </Button>
        </Box>
      </GlassPanel>

      {loading ? (
        <GlassPanel>
          <PanelLoader label="Loading device data" />
        </GlassPanel>
      ) : devices.length === 0 ? (
        <GlassPanel>
          <EmptyState
            icon={<SensorsRounded />}
            title="No devices available"
            description="Register endpoints to start exploring telemetry."
            minHeight={260}
          />
        </GlassPanel>
      ) : (
        <>
          <GlassPanel sx={{ p: 3 }}>
            <SectionTitle
              title="Telemetry Series"
              subtitle={`${history.series?.length || 0} samples in the selected ${range} window`}
              icon={<TimelineRounded sx={{ fontSize: 18 }} />}
            />
            <Box sx={{ mt: 2.5 }}>
              {chart.datasets.length && chart.datasets.some((dataset) => dataset.data.some((value) => value !== null)) ? (
                <HoloLineChart labels={chart.labels} datasets={chart.datasets} height={320} legend />
              ) : (
                <EmptyState
                  icon={<TimelineRounded />}
                  title="No telemetry in this window"
                  description="Choose a different time range or verify the endpoint is reporting."
                  minHeight={280}
                />
              )}
            </Box>
          </GlassPanel>

          <Grid container spacing={2.5}>
            <Grid item xs={12} lg={7}>
              <GlassPanel sx={{ p: 3, height: '100%' }}>
                <SectionTitle title="Latest Reading" subtitle={formatDateTime(latest?.timestamp)} icon={<SensorsRounded sx={{ fontSize: 18 }} />} />
                <Box sx={{ mt: 2, display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                  {latestRows.length === 0 && (
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <EmptyState title="No reading available" description="The endpoint has not reported data." minHeight={160} />
                    </Box>
                  )}
                  {latestRows.map((row) => (
                    <Box
                      key={row.key}
                      sx={{
                        p: 1.5,
                        borderRadius: 2.5,
                        border: `1px solid ${alpha(palette.primary, 0.14)}`,
                        background: alpha(palette.primary, 0.05),
                      }}
                    >
                      <Typography sx={{ fontSize: '0.62rem', letterSpacing: '0.14em', color: 'text.secondary' }}>
                        {row.key.toUpperCase()}
                      </Typography>
                      <Typography className="mono" sx={{ fontSize: '1.05rem', fontWeight: 500, mt: 0.4 }}>
                        {row.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </GlassPanel>
            </Grid>

            <Grid item xs={12} lg={5}>
              <GlassPanel sx={{ p: 3, height: '100%' }}>
                <SectionTitle title="Window Statistics" subtitle="Aggregates per parameter" icon={<TimelineRounded sx={{ fontSize: 18 }} />} />
                <Box sx={{ mt: 2, display: 'grid', gap: 1 }}>
                  {Object.entries(history.stats || {}).map(([key, stats]) => (
                    <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Typography sx={{ fontSize: '0.76rem', flex: 1, color: 'text.secondary' }}>{key}</Typography>
                      <Chip size="small" label={`min ${stats.min}`} sx={{ fontSize: '0.64rem', background: alpha('#334155', 0.26) }} />
                      <Chip size="small" label={`avg ${Number(stats.avg).toFixed(2)}`} sx={{ fontSize: '0.64rem', background: alpha(palette.primary, 0.14), color: palette.primary }} />
                      <Chip size="small" label={`max ${stats.max}`} sx={{ fontSize: '0.64rem', background: alpha(palette.secondary, 0.14), color: palette.secondary }} />
                    </Box>
                  ))}
                  {Object.keys(history.stats || {}).length === 0 && (
                    <EmptyState title="No statistics" description="No samples were recorded in this window." minHeight={140} />
                  )}
                </Box>
                <Divider sx={{ borderColor: alpha(palette.primary, 0.1), my: 2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StatusPill label={`${history.points || 0} samples`} tone="info" pulse={false} />
                  <StatusPill label={`${Object.keys(history.stats || {}).length} parameters`} tone="secondary" pulse={false} />
                </Box>
              </GlassPanel>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}
